/**
 * Overview Tab Component
 * Displays mission overview information
 */

import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Avatar,
  Chip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { StatusBadge, CopyableText } from '@/components/common';
import { missionTypeLabels } from '@/services/mock/mockMission';
import type { MissionDetail } from '@/types/mission';
import { formatGhostTierDisplay } from '@/utils/generalUtils';

interface OverviewTabProps {
  mission: MissionDetail;
}

export const OverviewTab: React.FC<OverviewTabProps> = ({ mission }) => {
  const theme = useTheme();

  const formatLocation = () => {
    if (!mission.location) return 'N/A';
    const parts = [
      mission.location.address,
      mission.location.city,
      mission.location.state,
      mission.location.zipCode,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : 'N/A';
  };

  // Helper function to get initials from name
  const getInitials = (name?: string): string => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Mission date-times are now pre-formatted by the service, just pass through
  const formatDateTime = (dateTimeString?: string) => {
    if (!dateTimeString) return 'N/A';
    return dateTimeString;
  };

  const formatRefundDecision = (decision?: string | null) => {
    if (!decision || decision === 'None') return 'N/A';
    if (decision === 'Denied') return 'No Refund';
    if (decision === 'Full') return 'Full Refund';
    if (decision === 'Partial') return 'Partial Refund';
    return decision;
  };

  const hasRefundMetadata = Boolean(mission.refundDecision && mission.refundDecision !== 'None');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}>
      {/* Mission Title */}
      {mission.title && (
        <Box>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              marginBottom: theme.spacing(1),
              fontWeight: 500,
            }}
          >
            Mission Title
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.text.primary,
              fontWeight: 500,
              fontSize: theme.typography.body1.fontSize,
            }}
          >
            {mission.title}
          </Typography>
        </Box>
      )}

      {/* Description - Moved to top as it's important */}
      {mission.description && (
        <Box>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              marginBottom: theme.spacing(1),
              fontWeight: 500,
            }}
          >
            Description
          </Typography>
          <Box
            sx={{
              padding: theme.spacing(2),
              backgroundColor: theme.customColors.searchBackground,
              borderRadius: 1,
              border: `1px solid ${theme.customColors.tableBorder}`,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.primary,
                lineHeight: 1.6,
                fontSize: theme.typography.body2.fontSize,
                whiteSpace: 'pre-wrap',
              }}
            >
              {mission.description}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Mission Details */}
      <Box>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            marginBottom: theme.spacing(2),
          }}
        >
          Mission Details
        </Typography>

        <Grid container spacing={2}>
          {/* Mission Code */}
          <Grid item xs={12} sm={6}>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: theme.typography.caption.fontSize,
                marginBottom: theme.spacing(0.5),
              }}
            >
              Mission Code:
            </Typography>
            <CopyableText
              text={mission.code}
              sx={{
                color: theme.palette.text.primary,
                fontWeight: 600,
                fontSize: theme.typography.body2.fontSize,
              }}
            />
          </Grid>

          {/* Mission Type */}
          <Grid item xs={12} sm={6}>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: theme.typography.caption.fontSize,
                marginBottom: theme.spacing(0.5),
              }}
            >
              Mission Type:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.primary,
                fontWeight: 500,
                fontSize: theme.typography.body2.fontSize,
              }}
            >
              {missionTypeLabels[mission.missionType]}
            </Typography>
          </Grid>

          {/* Ghost Tier */}
          {mission.ghostTier && (
            <Grid item xs={12} sm={6}>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: theme.typography.caption.fontSize,
                  marginBottom: theme.spacing(0.5),
                }}
              >
                Ghost Tier:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: 500,
                  fontSize: theme.typography.body2.fontSize,
                }}
              >
                {formatGhostTierDisplay(mission.ghostTier)}
              </Typography>
            </Grid>
          )}

          {/* Category */}
          {(mission.categoryName || mission.subcategoryName) && (
            <Grid item xs={12} sm={6}>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: theme.typography.caption.fontSize,
                  marginBottom: theme.spacing(0.5),
                }}
              >
                Category:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: 500,
                  fontSize: theme.typography.body2.fontSize,
                }}
              >
                {mission.subcategoryName || mission.categoryName || 'N/A'}
              </Typography>
            </Grid>
          )}

          {/* Created */}
          <Grid item xs={12} sm={6}>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: theme.typography.caption.fontSize,
                marginBottom: theme.spacing(0.5),
              }}
            >
              Created:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.primary,
                fontWeight: 500,
                fontSize: theme.typography.body2.fontSize,
              }}
            >
              {formatDateTime(mission.createdText)}
            </Typography>
          </Grid>

          {/* Last Updated */}
          <Grid item xs={12} sm={6}>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: theme.typography.caption.fontSize,
                marginBottom: theme.spacing(0.5),
              }}
            >
              Last Updated:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.primary,
                fontWeight: 500,
                fontSize: theme.typography.body2.fontSize,
              }}
            >
              {formatDateTime(mission.lastUpdatedText)}
            </Typography>
          </Grid>

          {/* Status */}
          <Grid item xs={12} sm={6}>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: theme.typography.caption.fontSize,
                marginBottom: theme.spacing(0.5),
              }}
            >
              Status:
            </Typography>
            <StatusBadge status={mission.status} />
          </Grid>

          {mission.reviewDecision && (
            <Grid item xs={12} sm={6}>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: theme.typography.caption.fontSize,
                  marginBottom: theme.spacing(0.5),
                }}
              >
                Review Decision:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: 500,
                  fontSize: theme.typography.body2.fontSize,
                }}
              >
                {mission.reviewDecision}
              </Typography>
            </Grid>
          )}

          {mission.latestPaymentStatus && (
            <Grid item xs={12} sm={6}>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: theme.typography.caption.fontSize,
                  marginBottom: theme.spacing(0.5),
                }}
              >
                Latest Payment Status:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: 500,
                  fontSize: theme.typography.body2.fontSize,
                }}
              >
                {mission.latestPaymentStatus}
              </Typography>
            </Grid>
          )}

          {mission.cancelledAtText && (
            <Grid item xs={12} sm={6}>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: theme.typography.caption.fontSize,
                  marginBottom: theme.spacing(0.5),
                }}
              >
                Cancelled At:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: 500,
                  fontSize: theme.typography.body2.fontSize,
                }}
              >
                {mission.cancelledAtText}
              </Typography>
            </Grid>
          )}

          {mission.cancelledBy && (
            <Grid item xs={12} sm={6}>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: theme.typography.caption.fontSize,
                  marginBottom: theme.spacing(0.5),
                }}
              >
                Cancelled By:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: 500,
                  fontSize: theme.typography.body2.fontSize,
                }}
              >
                {mission.cancelledBy}
              </Typography>
            </Grid>
          )}

          {hasRefundMetadata && (
            <Grid item xs={12} sm={6}>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: theme.typography.caption.fontSize,
                  marginBottom: theme.spacing(0.5),
                }}
              >
                Refund Decision:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: 500,
                  fontSize: theme.typography.body2.fontSize,
                }}
              >
                {formatRefundDecision(mission.refundDecision)}
              </Typography>
            </Grid>
          )}

          {hasRefundMetadata && mission.refundAmount !== undefined && mission.refundAmount !== null && (
            <Grid item xs={12} sm={6}>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: theme.typography.caption.fontSize,
                  marginBottom: theme.spacing(0.5),
                }}
              >
                Refund Amount:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: 500,
                  fontSize: theme.typography.body2.fontSize,
                }}
              >
                ${mission.refundAmount.toFixed(2)}
              </Typography>
            </Grid>
          )}

          {mission.reviewReason && (
            <Grid item xs={12}>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: theme.typography.caption.fontSize,
                  marginBottom: theme.spacing(0.5),
                }}
              >
                Rejection Reason:
              </Typography>
              <Box
                sx={{
                  padding: theme.spacing(1.5),
                  backgroundColor: theme.customColors.searchBackground,
                  borderRadius: 1,
                  border: `1px solid ${theme.customColors.tableBorder}`,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.primary,
                    fontWeight: 500,
                    fontSize: theme.typography.body2.fontSize,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {mission.reviewReason}
                </Typography>
              </Box>
            </Grid>
          )}

          {mission.cancellationReason && (
            <Grid item xs={12}>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: theme.typography.caption.fontSize,
                  marginBottom: theme.spacing(0.5),
                }}
              >
                Cancellation Reason:
              </Typography>
              <Box
                sx={{
                  padding: theme.spacing(1.5),
                  backgroundColor: theme.customColors.searchBackground,
                  borderRadius: 1,
                  border: `1px solid ${theme.customColors.tableBorder}`,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.primary,
                    fontWeight: 500,
                    fontSize: theme.typography.body2.fontSize,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {mission.cancellationReason}
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Location */}
          {mission.location && (
            <Grid item xs={12} sm={6}>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: theme.typography.caption.fontSize,
                  marginBottom: theme.spacing(0.5),
                }}
              >
                Location:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: 500,
                  fontSize: theme.typography.body2.fontSize,
                }}
              >
                {formatLocation()}
              </Typography>
            </Grid>
          )}

          {/* Timeframe */}
          {mission.timeframe && (mission.timeframe.startDate || mission.timeframe.endDate) && (
            <Grid item xs={12}>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: theme.typography.caption.fontSize,
                  marginBottom: theme.spacing(0.5),
                }}
              >
                Timeframe:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(0.5) }}>
                {mission.timeframe.startDate && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.primary,
                      fontWeight: 500,
                      fontSize: theme.typography.body2.fontSize,
                    }}
                  >
                    Start: {formatDateTime(mission.timeframe.startDate)}
                  </Typography>
                )}
                {mission.timeframe.endDate && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.primary,
                      fontWeight: 500,
                      fontSize: theme.typography.body2.fontSize,
                    }}
                  >
                    End: {formatDateTime(mission.timeframe.endDate)}
                  </Typography>
                )}
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>

      {mission.ghostName && (
        <Box>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              marginBottom: theme.spacing(1),
            }}
          >
            Ghost Information
          </Typography>
          <Box>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: theme.typography.caption.fontSize,
                marginBottom: theme.spacing(1.5),
              }}
            >
              Assigned Ghost:
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1.5), maxWidth: 400 }}>
              <Avatar
                sx={{
                  width: 48,
                  height: 48,
                  backgroundColor: theme.palette.grey[600],
                  color: theme.palette.common.white,
                  fontSize: theme.typography.body1.fontSize,
                  fontWeight: theme.typography.fontWeightMedium,
                }}
              >
                {getInitials(mission.ghostName)}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.primary,
                    fontSize: theme.typography.body2.fontSize,
                    fontWeight: 500,
                  }}
                >
                  {mission.ghostName}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: theme.typography.caption.fontSize,
                  }}
                >
                  Investigator
                </Typography>
              </Box>
              {mission.ghostId && (
                <Chip
                  label={mission.ghostId}
                  size="small"
                  sx={{
                    backgroundColor: theme.palette.grey[800],
                    color: theme.palette.common.white,
                    fontSize: theme.typography.caption.fontSize,
                    height: 24,
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>
      )}

      {mission.assetName && (
        <Box>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              marginBottom: theme.spacing(1),
            }}
          >
            Asset Basic Information
          </Typography>
          <Box>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: theme.typography.caption.fontSize,
                marginBottom: theme.spacing(1.5),
              }}
            >
              Asset ID:
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1.5), maxWidth: 400 }}>
              <Avatar
                sx={{
                  width: 48,
                  height: 48,
                  backgroundColor: theme.palette.secondary.main,
                  color: theme.palette.common.white,
                  fontSize: theme.typography.body1.fontSize,
                  fontWeight: theme.typography.fontWeightMedium,
                }}
              >
                {getInitials(mission.assetName)}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.primary,
                    fontSize: theme.typography.body2.fontSize,
                    fontWeight: 500,
                  }}
                >
                  {mission.assetName}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: theme.typography.caption.fontSize,
                  }}
                >
                  Asset
                </Typography>
              </Box>
              {mission.assetId && (
                <Chip
                  label={mission.assetId}
                  size="small"
                  sx={{
                    backgroundColor: theme.palette.secondary.dark,
                    color: theme.palette.common.white,
                    fontSize: theme.typography.caption.fontSize,
                    height: 24,
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>
      )}

      {mission.estimatedCompletionHours && (
        <Box>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              marginBottom: theme.spacing(1),
            }}
          >
            Mission Hours
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: theme.spacing(1) }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: theme.typography.h4.fontWeight,
                color: theme.palette.text.primary,
              }}
            >
              {mission.estimatedCompletionHours}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: theme.typography.body2.fontSize,
              }}
            >
              hrs
            </Typography>
          </Box>
        </Box>
      )}

      {(mission.ghostEarnings !== undefined || mission.platformFee !== undefined || mission.assetBookingFee !== undefined || mission.totalPrice !== undefined) && (
        <Box>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              marginBottom: theme.spacing(1),
            }}
          >
            Payment Information
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1.5), maxWidth: 400 }}>
            {mission.ghostEarnings !== undefined && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: theme.typography.body2.fontSize,
                  }}
                >
                  Ghost Earnings:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: theme.palette.text.primary,
                    fontSize: theme.typography.body2.fontSize,
                  }}
                >
                  ${mission.ghostEarnings.toFixed(2)}
                </Typography>
              </Box>
            )}
            {mission.platformFee !== undefined && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: theme.typography.body2.fontSize,
                  }}
                >
                  Platform Fee:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: theme.palette.text.primary,
                    fontSize: theme.typography.body2.fontSize,
                  }}
                >
                  ${mission.platformFee.toFixed(2)}
                </Typography>
              </Box>
            )}
            {mission.assetBookingFee !== undefined && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: theme.typography.body2.fontSize,
                  }}
                >
                  Asset Booking Fee:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: theme.palette.text.primary,
                    fontSize: theme.typography.body2.fontSize,
                  }}
                >
                  ${mission.assetBookingFee.toFixed(2)}
                </Typography>
              </Box>
            )}
            {mission.totalPrice !== undefined && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: theme.spacing(1),
                  paddingTop: theme.spacing(1.5),
                  borderTop: `1px solid ${theme.customColors.tableBorder}`,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                    fontSize: theme.typography.body2.fontSize,
                  }}
                >
                  Total Price:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.primary.main,
                    fontSize: theme.typography.body2.fontSize,
                  }}
                >
                  ${mission.totalPrice.toFixed(2)}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};
